/**
 * First-touch marketing attribution capture.
 *
 * @overview
 * Reads the ad-click signals present when a visitor lands, classifies them with
 * the shared vocabulary in `convex/lib/attribution`, and holds the result until
 * they check out — which may be several navigations, or several days, later.
 *
 * FIRST touch wins, deliberately. A visitor who arrives from a Meta ad, leaves,
 * and returns via a bookmark should still be credited to the ad that found
 * them; last-touch would hand that order to "organic" and make paid social look
 * like it bought nothing.
 *
 * @security Stores no PII — a channel enum, a campaign id, and Meta's click id.
 * Unlike the consent-gated analytics tags, this is first-party data used to
 * attribute our own revenue, and it never leaves our backend except as the
 * `fbc` match parameter on a conversion the customer's purchase already created.
 */

import {
  classifyAttribution,
  buildFbc,
  coerceAttributionSource,
  ORDER_ATTRIBUTION_SOURCES,
  type OrderAttributionSource,
} from '../../convex/lib/attribution';

const STORAGE_KEY = 'iv_attribution_v1';

/** How long a click stays creditable. Matches Meta's default 7-day window. */
const ATTRIBUTION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export interface CapturedAttribution {
  source: OrderAttributionSource;
  campaignId?: string;
  clickId?: string;
  capturedAt: number;
}

/** Read the stored first touch, or null when absent, corrupt, or expired. */
export function getStoredAttribution(): CapturedAttribution | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<CapturedAttribution>;
    if (typeof parsed.capturedAt !== 'number') return null;
    // An expired click is not attributable. Returning it anyway would credit
    // spend from weeks ago for an order it did not cause.
    if (Date.now() - parsed.capturedAt > ATTRIBUTION_WINDOW_MS) return null;

    return {
      source: coerceAttributionSource(parsed.source),
      campaignId: parsed.campaignId,
      clickId: parsed.clickId,
      capturedAt: parsed.capturedAt,
    };
  } catch {
    // Corrupt payload: treat as absent rather than throwing on every page load.
    return null;
  }
}

/**
 * Classify the current landing and store it if it is the first meaningful one.
 *
 * Returns whatever attribution is now in effect. Safe to call on every route
 * change: it only writes when there is something better to record.
 */
export function captureAttribution(
  search: string,
  referrer: string
): CapturedAttribution | null {
  const params = new URLSearchParams(search);
  const fbclid = params.get('fbclid');
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');

  const source = classifyAttribution({ fbclid, utmSource, utmMedium, referrer });
  const existing = getStoredAttribution();

  // First touch wins — but a bare "organic" placeholder must not block a real
  // ad click that arrives later in the same session, which is what happens when
  // a visitor lands on the homepage first and clicks an ad afterwards.
  const existingIsMeaningful =
    existing !== null && existing.source !== ORDER_ATTRIBUTION_SOURCES.ORGANIC;
  const incomingIsMeaningful = source !== ORDER_ATTRIBUTION_SOURCES.ORGANIC;

  if (existingIsMeaningful || (existing !== null && !incomingIsMeaningful)) {
    return existing;
  }

  const captured: CapturedAttribution = {
    source,
    campaignId: params.get('utm_campaign') ?? undefined,
    clickId: fbclid ?? undefined,
    capturedAt: Date.now(),
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch {
    // Storage unavailable; attribution lasts only for this pageview.
  }
  return captured;
}

/** Shape the checkout mutation expects. Omits keys rather than sending undefined. */
export function toOrderAttributionArgs(
  attribution: CapturedAttribution | null
): {
  attributionSource?: string;
  attributionCampaignId?: string;
  attributionClickId?: string;
} {
  if (!attribution) return {};
  return {
    attributionSource: attribution.source,
    ...(attribution.campaignId ? { attributionCampaignId: attribution.campaignId } : {}),
    ...(attribution.clickId ? { attributionClickId: attribution.clickId } : {}),
  };
}

/**
 * Read a cookie by name, or undefined. Never throws.
 *
 * Used for Meta's `_fbp` / `_fbc`, which the pixel sets once consent is given.
 * They are the browser's own identifiers — reading them is not extra tracking,
 * and if the visitor declined they simply do not exist.
 */
export function readCookie(name: string): string | undefined {
  try {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Identity signals for Meta's Conversions API match parameters.
 *
 * Gathered at the moment of the conversion because the server relay for
 * `Purchase` runs from the Stripe webhook, long after the browser is gone —
 * these get snapshotted onto the order and replayed. See convex/orders.ts.
 */
export interface MetaMatchSignals {
  /** Meta's browser id cookie. Also one of Meta's deduplication keys. */
  fbp?: string;
  /** Meta's click id, in `fb.1.<time>.<fbclid>` form — never a bare fbclid. */
  fbc?: string;
  clientUserAgent?: string;
}

/**
 * Collect what Meta can use to match this conversion to a person.
 *
 * Returns only keys that have values, so callers can spread it without sending
 * explicit `undefined`s into a Convex mutation.
 */
export function collectMetaMatchSignals(): MetaMatchSignals {
  const attribution = getStoredAttribution();
  const fbc = buildFbc(
    attribution?.clickId,
    attribution?.capturedAt ?? Date.now(),
    readCookie('_fbc')
  );
  const fbp = readCookie('_fbp');
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;

  return {
    ...(fbp ? { fbp } : {}),
    ...(fbc ? { fbc } : {}),
    ...(ua ? { clientUserAgent: ua } : {}),
  };
}

/** Test seam: forget the stored first touch. */
export function __clearStoredAttributionForTest(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
